import { SearchCriteria } from "../../pojo/SearchCriteria";
import { Item } from "../../pojo/Item";
import { S25Const } from "../../util/s25-const";
import { SearchService } from "./search.service";
import { S25Util } from "../../util/s25-util";
import { SubscriberSetUtil } from "./subscriber-sets/subscriber.set.util";
import { DataAccess } from "../../dataaccess/data.access";

export class SearchUtil {
    private static allPrefixes = Object.values(S25Const.itemId2LegacyName).flatMap((name) => [name, name + "s"]);

    private static PrefixedItemTypes = [
        Item.Ids.Event,
        Item.Ids.Organization,
        Item.Ids.Location,
        Item.Ids.Resource,
    ] as const;

    /**
     * Prefixes params when the object type for the param is not inferrable,
     * applies URI encoding to it, and returns it with `&` in front
     */
    public static param2Query({
        value,
        parameter,
        defaultNeedsPrefix = true,
        subjId,
        searchTypeId = subjId,
        encodeURI = true,
    }: {
        value: string | number;
        parameter: string;
        defaultNeedsPrefix?: boolean;
        subjId: number;
        searchTypeId?: number;
        encodeURI?: boolean;
    }) {
        const encoder = encodeURI ? encodeURIComponent : (a: any) => a;
        if (searchTypeId > 100) {
            searchTypeId -= 100;
        }
        const needsPrefix = defaultNeedsPrefix || subjId !== searchTypeId;
        const typeId = searchTypeId || subjId;
        const prefix = SearchUtil.PrefixedItemTypes.includes(typeId) ? S25Const.itemId2LegacyName[typeId] : "";
        if (!prefix || !needsPrefix || SearchUtil.isPrefixed(parameter)) {
            //eg, list
            return "&" + parameter + "=" + encoder(value);
        } else {
            //calendar, availability
            return `&${prefix}s_${parameter}=${encoder(value)}`;
        }
    }

    private static isPrefixed(paramName: string) {
        return SearchUtil.allPrefixes.some((prefix) => paramName.startsWith(prefix + "_") || paramName === prefix);
    }

    public static queryId2Query(
        queryId: number | string,
        defaultNeedsPrefix: boolean,
        subjId: number,
        searchTypeId: number,
    ) {
        return SearchUtil.param2Query({
            value: queryId,
            parameter: "query_id",
            defaultNeedsPrefix,
            subjId,
            searchTypeId,
        });
    }

    /**
     * Convert subscriber key to query string parameter for contacts
     * Parallel to queryId2Query but for subscriber sets
     */
    public static subscriberKey2Query(
        subscriberKey: number | string,
        defaultNeedsPrefix: boolean,
        subjId: number,
        searchTypeId: number,
    ) {
        return SearchUtil.param2Query({
            value: subscriberKey,
            parameter: "subscriber_key",
            defaultNeedsPrefix,
            subjId,
            searchTypeId,
        });
    }

    public static queryParam2Id(param: string) {
        return param && param.split && param.split("=").length > 1 && parseInt(param.split("=")[1]);
    }

    public static async createSearch(
        model: SearchCriteria.Model,
        searches: SearchCriteria.Searches,
        itemType: Item.Id,
        name?: string,
    ): Promise<{ id: number; name: string; tempIds: number[] }> {
        model = S25Util.deepCopy(model);
        name ||= `temp_${S25Util.generateQuickGUID()}`;
        const tempSearchSteps = model.step.filter(SearchUtil.isStepTemp);
        const tempSearches = await Promise.all(
            tempSearchSteps.map(async (step) => {
                const key = step.step_param[0].itemName;
                const newSearch = await SearchUtil.createSearch(searches[key], searches, itemType);
                step.step_param[0].itemId = newSearch.id;
                step.step_param[0].itemName = newSearch.name;
                return newSearch;
            }),
        );
        const newSearch = await SearchService.putJsonSearch(itemType, model, null, true, name, false, false);

        return {
            id: newSearch.queryId,
            name,
            tempIds: S25Util.array.flatten(tempSearches.map((search) => [search.id, search.tempIds])),
        };
    }

    public static getTempSearchQueryGenerator(
        model: SearchCriteria.Model,
        searches: SearchCriteria.Searches,
        itemType: Item.Id,
        defaultNeedsPrefix: boolean,
    ) {
        if (SearchService.usesSubscriptionSets(itemType)) {
            return SearchUtil.getTempSubscriberSetGenerator(model, itemType, defaultNeedsPrefix);
        }

        return async () => {
            const { id, tempIds } = await SearchUtil.createSearch(model, searches, itemType);
            const query = SearchUtil.queryId2Query(id, defaultNeedsPrefix, itemType, itemType);
            const postSearch = () => {
                // Delete all searches
                return Promise.all([id, ...tempIds].map((id) => SearchService.deleteSearch(id, itemType)));
            };

            return { query, postSearch };
        };
    }

    /**
     * Get a temporary subscriber set generator for contacts
     * Subscriber set properties are always avaialable as url parameters
     * running a subscriber set immediately after creation isn't consistently working
     */
    public static getTempSubscriberSetGenerator(
        model: SearchCriteria.Model,
        itemType: Item.Id,
        defaultNeedsPrefix: boolean,
    ) {
        return async () => {
            const filter = SubscriberSetUtil.getFilterFromStep(model.step || []);
            const queryParams = DataAccess.params(filter);
            // Prepend "&" to match expected format (same as &query_id=123 or &subscriber_key=123)
            const query = queryParams ? "&" + queryParams : "";

            const postSearch = () => {
                return Promise.resolve();
            };

            return { query, postSearch };
        };
    }

    public static isStepTemp(step: SearchCriteria.Step) {
        const stepType = Number(step.step_type_id); // Event search is 108, all others are X05
        const isSearch =
            stepType === SearchCriteria.StepType.Event.EventSearch || (stepType !== 105 && stepType % 100 === 5);
        return isSearch && step.step_param?.[0]?.itemName?.startsWith?.("temp_");
    }

    public static itemType2SearchTypeId(itemType: Item.Id) {
        return 10 + itemType;
    }
}
